import React, {FC, ReactElement} from "react";
import {
    Alert,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from "@mui/material";
import LoadingButton from "../../../components/button/LoadingButton";
import {observer} from "mobx-react";
import {ContractType, newEmployeeWizardState, TaxResidenceCountryType} from "../../../store/NewEmployeeWizardState";
import YesNoRadioGroup from "../../../components/input/YesNoRadioGroup";
import {ControlDivider} from "./NewEmployeeWizard";
import makeStyles from "@mui/styles/makeStyles";
import CountrySelector from "../../../components/selector/CountrySelector";
import NumberFormat from "react-number-format";
import {templateApi} from "../../../backend/TemplateApi";
import {saveFile} from "../../../utils/FileDownloadUtils";
import LoadingLink from "../../../components/LoadingLink";
import Tooltip from "../../../components/Tooltip";
import {Trans, useTranslation, withTranslation} from "react-i18next";

interface IProps {
    handleBack: () => void
    handleNext: () => void
}

const useStyles = makeStyles({
    countrySelector: {
        minWidth: "15rem"
    }
});

export const TaxDetailsAndTemplatesSection: FC<IProps> = observer(({handleNext, handleBack}): ReactElement => {
    const classes = useStyles();

    const {t} = useTranslation()

    const nextDisabled =
            !newEmployeeWizardState.taxResidenceCountryType ||
            (newEmployeeWizardState.taxResidenceCountryType === TaxResidenceCountryType.ESTONIA && newEmployeeWizardState.taxDeductionNeeded === undefined) ||
            (newEmployeeWizardState.taxResidenceCountryType === TaxResidenceCountryType.OTHER && !newEmployeeWizardState.taxResidenceCountryValue) ||
            newEmployeeWizardState.shouldWeRegisterWorker === undefined ||
            newEmployeeWizardState.contractTemplateNeeded === undefined ||
            false;

    const onDownloadContractTemplate = async () => {
        if (newEmployeeWizardState.contractType === ContractType.EMPLOYMENT) {
            const blob = await templateApi.downloadEmploymentContractTemplate(newEmployeeWizardState);
            saveFile(`employment_contract_${newEmployeeWizardState.name}-${newEmployeeWizardState.surname}.docx`, blob)
        } else {
            const blob = await templateApi.downloadBoardMemberContractTemplate(newEmployeeWizardState);
            saveFile(`board_member_contract_${newEmployeeWizardState.name}-${newEmployeeWizardState.surname}.docx`, blob)
        }
    };

    return <>
        <Grid container spacing={2}>
            <Grid item container xs={12} spacing={2} alignItems={"center"}>
                <Grid item>
                    <FormControl>
                        <FormLabel required><Trans i18nKey="new_employee.tax_and_templates.tax_residence">Tax residence
                            country</Trans></FormLabel>
                        <RadioGroup row value={newEmployeeWizardState.taxResidenceCountryType ?? ""}
                                    onChange={e => {
                                        // @ts-ignore
                                        newEmployeeWizardState.taxResidenceCountryType = e.target.value;
                                        newEmployeeWizardState.taxResidenceCountryValue = undefined
                                        newEmployeeWizardState.taxDeductionNeeded = undefined
                                        newEmployeeWizardState.taxResidenceStatementDocument = undefined
                                    }}>
                            <FormControlLabel value={TaxResidenceCountryType.ESTONIA} control={<Radio/>}
                                              label={t("new_employee.tax_and_templates.tax_residence_est", "Estonia")}/>
                            <FormControlLabel value={TaxResidenceCountryType.OTHER} control={<Radio/>}
                                              label={t("new_employee.tax_and_templates.tax_residence_other", "Other country")}/>
                            <FormControlLabel value={TaxResidenceCountryType.DONT_KNOW} control={<Radio/>}
                                              label={t("new_employee.tax_and_templates.tax_residence_unknown", "I don't know")}/>
                        </RadioGroup>
                    </FormControl>
                </Grid>
                {newEmployeeWizardState.taxResidenceCountryType === TaxResidenceCountryType.OTHER &&
                        <Grid item className={classes.countrySelector}>
                            <CountrySelector
                                    label={t("new_employee.tax_and_templates.tax_residence_country", "Country")}
                                    placeholder={t("new_employee.tax_and_templates.tax_residence_country_placeholder", "Tax residence country")}
                                    value={newEmployeeWizardState.taxResidenceCountryValue ?? ""}
                                    onChanged={code => newEmployeeWizardState.taxResidenceCountryValue = code}
                            />
                        </Grid>
                }
            </Grid>
            {newEmployeeWizardState.taxResidenceCountryType === TaxResidenceCountryType.DONT_KNOW &&
                    <Grid item xs={12}>
                        <Alert severity="warning">
                            <Typography variant="body2">
                                <Trans i18nKey="new_employee.tax_and_templates.ask_tax_residence">
                                    You should ask employee to provide tax residence statement from tax department
                                </Trans>
                            </Typography>
                        </Alert>
                    </Grid>
            }
            {newEmployeeWizardState.taxResidenceCountryType === TaxResidenceCountryType.ESTONIA &&
                    <>
                        <Grid item xs={12}>
                            <YesNoRadioGroup
                                    required={true}
                                    label={t("new_employee.tax_and_templates.tax_deduction", "Does employee need tax deduction?")}
                                    value={newEmployeeWizardState.taxDeductionNeeded ?? null}
                                    onChange={newValue => {
                                        newEmployeeWizardState.taxDeductionNeeded = newValue
                                        newEmployeeWizardState.taxDeductionFormDocument = undefined
                                    }}
                            />
                        </Grid>
                        {newEmployeeWizardState.taxDeductionNeeded === true &&
                                <Grid item xs={12}>
                                    <Alert severity="info">
                                        <Typography variant="subtitle2">
                                            <Trans i18nKey="new_employee.tax_and_templates.tax_deduction_info_note">
                                                Note that tax deduction can't be used in case employee's income is
                                                bigger than 1200 EUR per
                                                month
                                            </Trans>
                                        </Typography>
                                        <Typography variant="body2">
                                            <Trans i18nKey="new_employee.tax_and_templates.tax_deduction_template">
                                                You can download template clicking
                                            </Trans>{" "}
                                            <LoadingLink onClick={async () => {
                                                const blob = await templateApi.downloadTaxDeductionTemplate();
                                                saveFile("tax_deductible_form.docx", blob)
                                            }}><Trans i18nKey="common.here">here</Trans></LoadingLink>
                                        </Typography>
                                    </Alert>
                                </Grid>
                        }
                    </>
            }
            <Grid item container xs={12} spacing={2} alignItems={"center"}>
                <Grid item>
                    <YesNoRadioGroup
                            required={true}
                            label={t("new_employee.tax_and_templates.should_we_register_employee", "Should we inform employee register on your behalf?")}
                            value={newEmployeeWizardState.shouldWeRegisterWorker ?? null}
                            onChange={newValue => {
                                newEmployeeWizardState.shouldWeRegisterWorker = newValue
                            }}
                    />
                </Grid>
                <Grid item>
                    <Tooltip variant="subtitle1">
                        <Trans i18nKey={"new_employee.tax_and_templates.should_we_register_employee_tip"}>
                            If you select <b>Yes</b> - your accountant will be notified and we will register new
                            employee in the employee register.<br/>
                            You don't have to worry about it in this case
                        </Trans>
                    </Tooltip>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <YesNoRadioGroup
                        required={true}
                        label={t("new_employee.tax_and_templates.need_contract_template", "Do you need contract template?")}
                        value={newEmployeeWizardState.contractTemplateNeeded ?? null}
                        onChange={newValue => {
                            newEmployeeWizardState.contractTemplateNeeded = newValue
                        }}
                />
            </Grid>
            {newEmployeeWizardState.contractTemplateNeeded === true && <>
                <Grid item container xs={12} alignItems={"flex-end"}>
                    <Grid item xs={12} md={5}>
                        <TextField
                                fullWidth
                                required
                                label={t("new_employee.tax_and_templates.countersigning_person_name", "Countersigning person full name")}
                                placeholder={"John Doe"}
                                value={newEmployeeWizardState.countersigningPersonFullName ?? ""}
                                onChange={e => newEmployeeWizardState.countersigningPersonFullName = e.target.value}
                        />
                    </Grid>
                    <Grid item>
                        <Tooltip variant="subtitle1">
                            <Trans i18nKey="new_employee.tax_and_templates.countersigning_person_name_tip">
                                Person who signs contract from employer side. Usually it's a board member or HR director
                            </Trans>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Grid item container xs={12} alignItems={"flex-end"}>
                    <Grid item xs={12} md={5}>
                        <NumberFormat
                                required
                                label={t("new_employee.tax_and_templates.day_of_payment", `Day of payment (between 1 and 28)`)}
                                fullWidth
                                customInput={TextField}
                                value={newEmployeeWizardState.dayOfPayment}
                                decimalScale={0}
                                allowNegative={false}
                                onValueChange={(values) => {
                                    newEmployeeWizardState.dayOfPayment = values.value;
                                }}
                                isNumericString
                                error={newEmployeeWizardState.dayOfPayment !== undefined && (Number(newEmployeeWizardState.dayOfPayment) <= 0 || Number(newEmployeeWizardState.dayOfPayment) > 28)}
                        />
                    </Grid>
                    <Grid item>
                        <Tooltip variant="subtitle1">
                            <Trans i18nKey="new_employee.tax_and_templates.day_of_payment_tip">
                                By this day employer should submit payment order to the bank to transfer employee's
                                salary
                            </Trans>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Grid item container xs={12}>
                    <Grid item xs={12} md={5}>
                        <TextField
                                fullWidth
                                required
                                label={t("new_employee.tax_and_templates.iban", "Employee IBAN")}
                                placeholder={"EE382200221020145685"}
                                value={newEmployeeWizardState.employeeIban ?? ""}
                                onChange={e => newEmployeeWizardState.employeeIban = e.target.value}
                        />
                    </Grid>
                </Grid>
                <Grid item container xs={12} alignItems={"center"}>
                    <Grid item xs={12} md={5}>
                        <TextField
                                fullWidth
                                required
                                label={t("new_employee.tax_and_templates.duties_description", "Employee duties short description")}
                                placeholder={"As credit manager your responsibilities include creating credit scoring models, setting loan terms and determining interest rates."}
                                multiline
                                minRows={3}
                                maxRows={3}
                                value={newEmployeeWizardState.employeeDutiesDescription ?? ""}
                                onChange={e => newEmployeeWizardState.employeeDutiesDescription = e.target.value}
                        />
                    </Grid>
                </Grid>
                {newEmployeeWizardState.employeeIban && newEmployeeWizardState.employeeDutiesDescription && newEmployeeWizardState.countersigningPersonFullName && newEmployeeWizardState.dayOfPayment && <>
                    <Grid item xs={12}>
                        <Alert severity="info">
                            <Typography variant="body2">
                                <Trans i18nKey="new_employee.tax_and_templates.check_before_signing">
                                    After downloading contract template please check it carefully and sign.
                                </Trans>
                            </Typography>
                            <Typography variant="subtitle2">
                                <Trans i18nKey="new_employee.tax_and_templates.correctness_wavier">
                                    Please be aware that we are not responsible for the contract correctness. This
                                    template were built based on contract that we are using with our
                                    employees.
                                </Trans>
                            </Typography>
                            <Typography variant="subtitle2">
                                <Trans i18nKey="new_employee.tax_and_templates.only_for_help_wavier">
                                    Template you will download is designed only to help you, all legal risks are under
                                    your own responsibility.
                                </Trans>
                            </Typography>
                            <Typography variant="h6">
                                <Trans i18nKey="new_employee.tax_and_templates.download_here">You can download template
                                    clicking</Trans>{" "}
                                <LoadingLink onClick={onDownloadContractTemplate}>
                                    <Trans i18nKey="common.here">here</Trans>
                                </LoadingLink>
                            </Typography>
                        </Alert>
                    </Grid>
                </>
                }

            </>}
        </Grid>
        <ControlDivider/>
        <Grid container spacing={1}>
            <Grid item>
                <Button variant="contained" color="secondary" onClick={handleBack}><Trans
                        i18nKey="common.back">Back</Trans></Button>
            </Grid>
            <Grid item>
                <LoadingButton disabled={nextDisabled} variant="contained" color="primary" onClick={handleNext}><Trans
                        i18nKey="common.next">Next</Trans></LoadingButton>
            </Grid>
        </Grid>
    </>
});

export default withTranslation()(TaxDetailsAndTemplatesSection);